import React, { useState } from 'react'
import { useCookies } from 'react-cookie'
import Icon from 'components/elements/Icon'

import RequestService from 'services/request.service'
import CookieService from 'services/cookie.service'
import BookmarkService from 'services/bookmark.service'
import ToastService from 'services/toast.service'

import { useActionless, useAppSelector } from 'redux/hooks'
import { TOGGLE_MODAL } from 'redux/active/actions/types'

import { DeckVoteResponseType } from 'services/apiTypes/deck.types'
import { VOTE_USER_INPUT } from 'types/comments'
import { SET_DECK_STATE } from 'redux/deck/actions/types'
import { useCanEditDeck } from 'redux/deck/selectors'

import DeckDate from 'components/deckPage/header/DeckDate'
import Share from 'components/deckPage/header/Share'
import { Button } from 'components/deckPage/header/BannerButton'

import { simplifyNumber } from 'utils/formatting.utils'

import styles from './shareAndSocialControls.module.scss'

function pointDifferential(upvoted: boolean, removed: boolean): number {
  if (upvoted && removed) return -1
  if (upvoted && !removed) return 1
  if (!upvoted && removed) return 1
  if (!upvoted && !removed) return -1

  return 0
}

type Props = {
  className?: string
}

const ShareAndSocailControls = ({ className }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [loading, setLoading] = useState(false)

  const deckId = useAppSelector(state => state.deck.id)
  const voteInput = useAppSelector(state => state.deck.voteInput)
  const points = useAppSelector(state => state.deck.points)
  const canEdit = useCanEditDeck()
  const bookmarked = useAppSelector(state => state.deck.bookmarked)
  const viewCount = useAppSelector(state => state.deck.viewCount)

  const [toggleModal, setDeckState] = useActionless(TOGGLE_MODAL, SET_DECK_STATE)

  const upvoted = voteInput === VOTE_USER_INPUT.UP

  const handleOpenCollaborative = () => toggleModal({ modalName: 'collaborators', flag: true })

  const handleVote = async (up: boolean) => {
    const userId = CookieService.get('tbId')

    if (loading) return
    if (!userId) return toggleModal({ modalName: 'login', flag: true })

    const remove = (up && voteInput === VOTE_USER_INPUT.UP) || (!up && voteInput === VOTE_USER_INPUT.DOWN)

    // Just update the points, who cares what the request does. Feels better for the user this way. If the request fails, it's not a huge deal
    setDeckState({
      voteInput: remove ? VOTE_USER_INPUT.NONE : up ? VOTE_USER_INPUT.UP : VOTE_USER_INPUT.DOWN,
      points: points + pointDifferential(up, remove),
    })

    setLoading(true)

    RequestService.put<DeckVoteResponseType>('/api/decks/' + deckId + '/vote/', { up, remove })
      .catch(err => console.error(err))
      .finally(() => setLoading(false))
  }

  const handleToggleBookmark = () => {
    if (bookmarked)
      return BookmarkService.remove(deckId)
        .then(() => ToastService.create('Bookmark removed', 'Bookmark service', 'success'))
        .then(() => setDeckState({ bookmarked: false }))
        .catch(() => ToastService.create('Unable to delete bookmark', 'Bookmark service'))

    return BookmarkService.create(deckId)
      .then(() => ToastService.create('Deck bookmarked', 'Bookmark service', 'success'))
      .then(() => setDeckState({ bookmarked: true }))
      .catch(() => ToastService.create('Unable to create bookmark', 'Bookmark service'))
  }

  return (
    <div className={`${styles.container} ${className}`}>
      {!!deckId && (
        <>
          <Button
            icon={upvoted ? 'thumbs up' : 'thumbs up outline'}
            label={points > 1 ? `${points}` : '1'}
            onClick={() => handleVote(true)}
          />
          {/* Removing dislike button */}
          {/* <Button
            noTextOnSmall
            icon={downvoted ? 'thumbs down' : 'thumbs down outline'}
            label={points < 0 ? `${points}` : 'Dislike'}
            onClick={() => handleVote(false)}
          /> */}
          <Share />
          {!!Number(userId) && (
            <Button
              noTextOnSmall
              className={bookmarked ? styles.bookmarked : ''}
              icon={bookmarked ? 'bookmark' : 'bookmark outline'}
              label={bookmarked ? 'Remove Bookmark' : 'Bookmark'}
              onClick={() => handleToggleBookmark()}
            />
          )}
          {canEdit && <Button noTextOnSmall icon="users" label="Collaborate" onClick={handleOpenCollaborative} />}
        </>
      )}

      <div className={styles.metaInfo}>
        <div className={styles.viewCount}>
          <Icon name="eye" /> <span>{simplifyNumber(viewCount)}</span>
        </div>
        <div className={styles.updatedAt}>
          <DeckDate />
        </div>
      </div>
    </div>
  )
}

export default ShareAndSocailControls
